/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useCallback } from 'react';
import {
  getFileDiff,
  commitChanges,
  syncWithRemoteVelocity,
} from '../../lib/api';
import type { WorktreeInfo, WorktreeStatus, SourceControlProvider } from '../../components/changes';
import type { SkillShopService } from '../../services/skillShopService';

interface ActiveProduct {
  repositoryUrl?: string | null;
  repoPath?: string | null;
  title?: string;
}

interface GitLabConfig {
  accessToken?: string;
  instanceUrl?: string;
}

interface ToastFn {
  (opts: { title: string; description: string; variant?: 'destructive' }): void;
}

interface UseSyncModesParams {
  activeProduct: ActiveProduct | null;
  selectedWorktree: WorktreeInfo | null;
  status: WorktreeStatus | null;
  selectedFiles: Set<string>;
  skillShopService: SkillShopService;
  toast: ToastFn;
  loadStatus: () => Promise<void>;
  loadCommitHistory: () => Promise<void>;
  loadSyncToMainAnalysis: () => Promise<void>;
  loadRemoteVelocityStatus: () => Promise<void>;
  handleSync: () => Promise<void>;
  handleSyncToMain: (forceAction?: 'auto-merge' | 'create-pr') => Promise<void>;
  accessToken: string | undefined;
  sourceControlProvider: SourceControlProvider;
  gitlabConfig: GitLabConfig | null;
  setCommitting: (v: boolean) => void;
  setSyncing: (v: boolean) => void;
}

export function useSyncModes({
  activeProduct,
  selectedWorktree,
  status,
  selectedFiles,
  skillShopService,
  toast,
  loadStatus,
  loadCommitHistory,
  loadSyncToMainAnalysis,
  loadRemoteVelocityStatus,
  handleSync,
  handleSyncToMain,
  accessToken,
  sourceControlProvider,
  gitlabConfig,
  setCommitting,
  setSyncing,
}: UseSyncModesParams) {
  // Helper: auto-commit changes with AI-generated message
  const autoCommitChanges = useCallback(async (): Promise<boolean> => {
    if (!activeProduct?.repositoryUrl || !selectedWorktree || !status?.files) return false;

    const filesToCommit = selectedFiles.size > 0
      ? status.files.filter(f => selectedFiles.has(f.path))
      : status.files;

    if (filesToCommit.length === 0) return false;

    setCommitting(true);
    try {
      const filesToDiff = filesToCommit.slice(0, 10);
      const diffPromises = filesToDiff.map(async (file) => {
        try {
          const result = await getFileDiff(
            activeProduct.repositoryUrl!,
            selectedWorktree.path,
            file.path
          );
          if (result.success) {
            return `=== ${file.path} ===\n${result.diff || '(no diff available)'}`;
          }
          return `=== ${file.path} ===\n(failed to load diff)`;
        } catch {
          return `=== ${file.path} ===\n(failed to load diff)`;
        }
      });

      const diffs = await Promise.all(diffPromises);
      const combinedDiffs = diffs.join('\n\n');

      let generatedMessage = 'Update product data';
      const messageResult = await skillShopService.generateCommitMessage(
        filesToCommit.map(f => ({ path: f.path, status: f.status })),
        combinedDiffs
      );
      if (messageResult.success && messageResult.message) {
        generatedMessage = messageResult.message;
      }

      const commitResult = await commitChanges(
        activeProduct.repositoryUrl,
        selectedWorktree.path,
        generatedMessage,
        selectedFiles.size > 0 ? Array.from(selectedFiles) : undefined
      );

      if (!commitResult.success) {
        throw new Error(commitResult.error || 'Failed to commit changes');
      }

      toast({
        title: 'Changes Saved',
        description: `Committed ${filesToCommit.length} file(s)`
      });

      return true;
    } catch (error: any) {
      console.error('[Changes] Auto-commit error:', error);
      toast({
        title: 'Commit Failed',
        description: error.message || 'Failed to save changes',
        variant: 'destructive'
      });
      return false;
    } finally {
      setCommitting(false);
    }
  }, [activeProduct, selectedWorktree, status, selectedFiles, skillShopService, toast, setCommitting]);

  // Simple mode sync: auto-commit (if changes) + sync with main
  const handleSimpleModeSync = useCallback(async () => {
    if (!activeProduct?.repositoryUrl || !selectedWorktree) return;

    const hasUncommittedChanges = status?.files && status.files.length > 0;

    if (hasUncommittedChanges) {
      console.log('[Changes] Simple mode: auto-generating commit message...');
      const committed = await autoCommitChanges();
      if (!committed && hasUncommittedChanges) return;

      await loadStatus();
      await loadCommitHistory();
      await loadSyncToMainAnalysis();
    }

    // Pull from main
    console.log('[Changes] Simple mode: pulling from main...');
    await handleSync();

    // Push to main
    if (accessToken) {
      console.log('[Changes] Simple mode: pushing to main...');
      await handleSyncToMain();
    } else {
      console.log('[Changes] Simple mode: skipping push (no access token)');
      toast({
        title: 'Sync Complete (Local Only)',
        description: `Configure ${sourceControlProvider === 'github' ? 'GitHub' : 'GitLab'} in Settings to push changes to remote`,
      });
    }
  }, [
    activeProduct,
    selectedWorktree,
    status,
    autoCommitChanges,
    toast,
    loadStatus,
    loadCommitHistory,
    loadSyncToMainAnalysis,
    handleSync,
    handleSyncToMain,
    accessToken,
    sourceControlProvider
  ]);

  // Velocity mode sync: auto-commit + push to origin/velocity
  const handleVelocityModeSync = useCallback(async () => {
    if (!activeProduct?.repositoryUrl || !selectedWorktree) return;

    const hasUncommittedChanges = status?.files && status.files.length > 0;

    if (hasUncommittedChanges) {
      console.log('[Changes] Velocity mode: auto-generating commit message...');
      const committed = await autoCommitChanges();
      if (!committed && hasUncommittedChanges) return;

      await loadStatus();
      await loadCommitHistory();
    }

    // Sync with remote velocity (pull then push)
    if (accessToken) {
      console.log('[Changes] Velocity mode: syncing with remote velocity...');
      setSyncing(true);
      try {
        const result = await syncWithRemoteVelocity(
          activeProduct.repositoryUrl,
          accessToken,
          true,
          sourceControlProvider,
          gitlabConfig?.instanceUrl
        );

        if (!result.success) {
          if (result.action === 'conflicts') {
            toast({
              title: 'Sync Conflicts',
              description: result.message || 'Conflicts with remote - pull and resolve first',
              variant: 'destructive'
            });
          } else {
            throw new Error(result.error || 'Failed to sync with remote');
          }
        } else {
          const pulledMsg = result.pulledCommits ? `pulled ${result.pulledCommits}` : '';
          const pushedMsg = result.pushedCommits ? `pushed ${result.pushedCommits}` : '';
          const syncDetails = [pulledMsg, pushedMsg].filter(Boolean).join(', ');

          toast({
            title: 'Changes Saved',
            description: syncDetails ? `Synced with remote (${syncDetails} commits)` : 'All changes saved to remote'
          });
        }

        await loadRemoteVelocityStatus();
        await loadStatus();
        await loadCommitHistory();
      } catch (error: any) {
        console.error('[Changes] Velocity mode sync error:', error);
        toast({
          title: 'Sync Failed',
          description: error.message || 'Failed to sync with remote velocity',
          variant: 'destructive'
        });
      } finally {
        setSyncing(false);
      }
    } else {
      console.log('[Changes] Velocity mode: skipping remote sync (no access token)');
      toast({
        title: 'Changes Saved Locally',
        description: `Configure ${sourceControlProvider === 'github' ? 'GitHub' : 'GitLab'} in Settings to sync with remote`,
      });
    }
  }, [
    activeProduct,
    selectedWorktree,
    status,
    autoCommitChanges,
    toast,
    loadStatus,
    loadCommitHistory,
    loadRemoteVelocityStatus,
    accessToken,
    sourceControlProvider,
    gitlabConfig,
    setSyncing
  ]);

  return {
    handleSimpleModeSync,
    handleVelocityModeSync,
  };
}
